import React, { useMemo } from "react";
import Dialog from "@material-ui/core/Dialog";
import { makeStyles } from "@material-ui/core/styles";
import DialogContent from "@material-ui/core/DialogContent";
import { Fetcher } from "./Fetcher";
import { grey } from "@material-ui/core/colors";
import { getLicenseDetails } from "../services/api/subscriptionLicenses.api";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import { LicenseDetailsResponse } from "../models/SubscriptionLicenses";
import { GridValueDisplayList } from "./GridValueDisplayList";
import { DateDisplay } from "./DateDisplay";

export interface SubscriptionLicenseDetailsDialogProps {
	licenseId: string;
	detailsOpen: boolean;
	setOpen: (open: boolean) => void;
	subscriptionId: string;
	productId: string;
}

const useStyles = makeStyles((theme) => ({
	appBar: {
		position: "relative",
	},
	tableWrapper: {
		margin: theme.spacing(0.5),
		minHeight: 800,
		maxWidth: 1200,
		overflow: "hidden",
		borderBottomColor: grey[100],
		borderBottomWidth: 1,
		borderBottomStyle: "solid",
	},
	dialogPaper: {
		minHeight: "95vh",
		maxHeight: "100vh",
	},
	title: {
		marginLeft: theme.spacing(2),
		flex: 1,
	},
	dialogBody: {
		margin: theme.spacing(4),
	},
}));

const renderData = (license: LicenseDetailsResponse) => {
	const uniqueResourcesList = (
		<div>
			{license.uniqueResources.map((resource) => {
				return <div key={resource}>{resource}</div>;
			})}
		</div>
	);
	const displayProps = [
		{
			label: "Product Name",
			node: license.productName,
		},
		{
			label: "Product Tier Name",
			node: license.productTierName,
		},
		{
			label: "License Type Name",
			node: license.licenseTypeName,
		},
		{
			label: "Total License Checks",
			node: license.licenseCheckNumberTotal,
		},
		{
			label: "Successful License Checks",
			node: license.licenseCheckNumberSuccess,
		},
		{
			label: "Failed License Checks",
			node: license.licenseCheckNumberFail,
		},
		{
			label: "Total Usage Reports",
			node: license.usageReportNumberTotal,
		},
		{
			label: "Successful Usage Reports",
			node: license.usageReportNumberSuccess,
		},
		{
			label: "Failed Usage Reports",
			node: license.usageReportNumberFail,
		},
		{
			label: "Number of Unique Resources",
			node: license.uniqueResourcesNumber,
		},
		{
			label: "Unique Resources",
			node: uniqueResourcesList,
		},
	];
	return (
		<div style={{ margin: 10 }} data-testid={"license-details-display-list"}>
			<GridValueDisplayList
				displayItems={[
					{
						label: "Start Date",
						node: <DateDisplay date={license.startDate} fontSize={16} />,
					},
					{
						label: "End Date",
						node: <DateDisplay date={license.endDate} fontSize={16} />,
					},
				]}
			/>
			<GridValueDisplayList itemSpacing_xs={12} itemSpacing_sm={6} displayItems={displayProps}></GridValueDisplayList>
		</div>
	);
};

/**
 * Twilio call and chat details dialog
 * @param props
 */
export const SubscriptionLicenseDetailsDialog = (props: SubscriptionLicenseDetailsDialogProps) => {
	const classes = useStyles();

	return (
		<Dialog
			open={props.detailsOpen}
			onClose={() => {
				props.setOpen(false);
			}}
			classes={{ paper: classes.dialogPaper }}
			fullWidth={true}
			maxWidth={"xl"}
		>
			<AppBar color="default" className={classes.appBar}>
				<Toolbar>
					<IconButton edge="start" color="inherit" onClick={() => props.setOpen(false)} aria-label="close">
						<CloseIcon />
					</IconButton>
					<Typography variant="h6" className={classes.title}>
						{`License Details: ${props.licenseId}`}
					</Typography>
				</Toolbar>
			</AppBar>
			<DialogContent>
				{useMemo(() => {
					return (
						<Fetcher action={getLicenseDetails(props.productId, props.subscriptionId, props.licenseId)}>
							{(response) => renderData(response.data)}
						</Fetcher>
					);
				}, [props.licenseId, props.productId, props.subscriptionId])}
			</DialogContent>
		</Dialog>
	);
};
